import { useCallback, useRef, useEffect } from "react";
import { useHomeData } from "./useHomeData";
import { PaginatedPatientsParams } from "../../services/getFunctions";

export interface FilterState {
  code_like?: string;
  code_must_equal?: boolean;
  gender_equals?: string;
  date_of_birth_less_than?: string;
  date_of_birth_greater_than?: string;
  admission_less_than?: string;
  admission_greater_than?: string;
  discharge_less_than?: string;
  discharge_greater_than?: string;
  clinician_username_like?: string;
  clinician_username_must_equal?: boolean;
  labels?: { key: string; value: string }[];
  report_state?: "NO_REPORT" | "UNREAD_REPORT" | "ALL_REPORT_READ";
}

export type SortField =
  | "CODE"
  | "GENDER"
  | "DATE_OF_BIRTH"
  | "CREATED"
  | "ADMISSION"
  | "DISCHARGE"
  | "CLINICIAN_USERNAME";

interface SortState {
  sort?: SortField;
  order?: "ASC" | "DESC";
}

/**
 * A hook to manage the states and interactions of PatientTable
 */
export const usePatientTableState = () => {
  const {
    patients,
    pagination,
    diagnoses,
    clinicians,
    loadPatients,
    refreshCurrentPage,
    isLoading,
  } = useHomeData();

  //use ref to save the filters and sort
  const filtersRef = useRef<FilterState>({});
  const sortRef = useRef<SortState>({});
  //add ref to update the pagination value
  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  /**
   * build complete parameters for getPaginatedPatients API
   * use ref value to avoid closure
   */
  const buildParams = useCallback((): Partial<PaginatedPatientsParams> => {
    const params: Partial<PaginatedPatientsParams> = {};
    const filters = filtersRef.current;
    const sort = sortRef.current;

    // Sort params
    if (sort.sort) params.sort = sort.sort;
    if (sort.order) params.order = sort.order;

    // Filter params
    if (filters.code_like) params.code_like = filters.code_like;
    if (filters.code_must_equal !== undefined)
      params.code_must_equal = filters.code_must_equal;
    if (filters.gender_equals) params.gender_equals = filters.gender_equals;
    if (filters.date_of_birth_less_than)
      params.date_of_birth_less_than = filters.date_of_birth_less_than;
    if (filters.date_of_birth_greater_than)
      params.date_of_birth_greater_than = filters.date_of_birth_greater_than;
    if (filters.admission_less_than)
      params.admission_less_than = filters.admission_less_than;
    if (filters.admission_greater_than)
      params.admission_greater_than = filters.admission_greater_than;
    if (filters.discharge_less_than)
      params.discharge_less_than = filters.discharge_less_than;
    if (filters.discharge_greater_than)
      params.discharge_greater_than = filters.discharge_greater_than;
    if (filters.clinician_username_like)
      params.clinician_username_like = filters.clinician_username_like;
    if (filters.clinician_username_must_equal !== undefined)
      params.clinician_username_must_equal =
        filters.clinician_username_must_equal;
    if (filters.labels?.length) params.labels = filters.labels;
    if (filters.report_state) params.report_state = filters.report_state;

    return params;
  }, []);

  // load page with params
  const loadPage = useCallback(
    (page: number, pageSize: number) => {
      const params = buildParams();
      loadPatients(page, pageSize, params);
    },
    [buildParams, loadPatients],
  );

  // if the filter is updated, go back to first page
  const updateFilters = useCallback(
    (newFilters: Partial<FilterState>) => {
      filtersRef.current = { ...filtersRef.current, ...newFilters };
      loadPage(1, paginationRef.current.pageSize);
    },
    [loadPage],
  );

  //clear filter
  const clearFilter = useCallback(
    (...filterKeys: (keyof FilterState)[]) => {
      const newFilters = { ...filtersRef.current };
      filterKeys.forEach((key) => delete newFilters[key]);
      filtersRef.current = newFilters;
      loadPage(1, paginationRef.current.pageSize);
    },
    [loadPage],
  );

  /**
   * Update or clear sorting
   * @param field - Sort field name
   * @param order - Sort order (asc/desc)
   */
  const updateSort = useCallback(
    (field?: SortField, order?: "ASC" | "DESC") => {
      // clear the sorting status with null, and return to first page
      if (field === undefined || order === undefined) {
        sortRef.current = {};
        const current = paginationRef.current;
        loadPage(1, current.pageSize);
      } else {
        sortRef.current = { sort: field, order: order };
      }

      const current = paginationRef.current;
      loadPage(1, current.pageSize);
    },
    [loadPage],
  );

  /**
   * update the server-side date filter
   * @param field - date field
   * @param lessThan - less_than API param
   * @param greaterThan - greater_than API param
   */
  const updateDateFilter = useCallback(
    (
      field: "date_of_birth" | "admission" | "discharge",
      lessThan?: string,
      greaterThan?: string,
    ) => {
      if (!lessThan && !greaterThan) {
        clearFilter(
          `${field}_less_than` as keyof FilterState,
          `${field}_greater_than` as keyof FilterState,
        );
        return;
      }

      const newFilters: Partial<FilterState> = {};

      newFilters[`${field}_less_than`] = lessThan;
      newFilters[`${field}_greater_than`] = greaterThan;
      updateFilters(newFilters);
    },
    [updateFilters, clearFilter],
  );

  //update string or select filters
  const updateFieldFilter = useCallback(
    (field: keyof FilterState, value?: string) => {
      if (value === undefined || value === "") {
        clearFilter(field);
      } else {
        updateFilters({ [field]: value });
      }
    },
    [updateFilters, clearFilter],
  );

  const updateLabelFilter = useCallback(
    (value?: string) => {
      if (!value) {
        clearFilter("labels");
      } else {
        updateFilters({ labels: [{ key: "CLUSTER", value }] });
      }
    },
    [updateFilters, clearFilter],
  );

  const updateReportStateFilter = useCallback(
    (value?: string) => {
      if (!value) {
        clearFilter("report_state");
      } else {
        updateFilters({ report_state: value as FilterState["report_state"] });
      }
    },
    [updateFilters, clearFilter],
  );

  /**
   * Handle page size change
   * When page size changes, reset to page 1
   */
  const handlePageChange = useCallback(
    (newPage: number) => {
      const currentPageSize = paginationRef.current.pageSize;
      loadPage(newPage, currentPageSize);
    },
    [loadPage],
  );

  //change the pageSize and go back to 1st page
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      loadPage(1, newPageSize);
    },
    [loadPage],
  );

  return {
    // data
    patients,
    pagination,
    diagnoses,
    clinicians,
    isLoading,

    // functions
    loadPage,
    updateFilters,
    updateSort,
    clearFilter,
    refreshCurrentPage,
    handlePageChange,
    handlePageSizeChange,
    updateFieldFilter,
    updateDateFilter,
    updateLabelFilter,
    updateReportStateFilter,
  };
};
